/**
 * citizenshipPasskeyChain.ts
 *
 * Browser-side orchestration for smart-wallet agents (Case A:
 * passkey.account.accountAddress === agent.address) to run the
 * relinquish → apply → pay → confirm residency chain without MCP.
 */

import type { PasskeySigner } from './profileService';
import {
  buildApplyTx,
  buildPaymentTx,
  buildRelinquishTx,
  confirmCitizenshipWithPasskey,
  getResidencySnapshot,
  submitSignedCitizenshipTx,
  type BuiltTx,
} from './citizenshipService';
import type { SubmitResult } from './agentService';

/** ~30 days at ~5 s/ledger close. */
export const RESIDENCY_TTL_LEDGERS = 518_400;

export type ResidencyChainStep = 'relinquish' | 'apply' | 'confirm' | 'poll';

export type ResidencyChainStepStatus = 'pending' | 'in_progress' | 'done' | 'skipped' | 'failed';

export type ResidencyChainStepHandler = (
  step: ResidencyChainStep,
  status: ResidencyChainStepStatus,
  detail?: string,
) => void;

export function assertPasskeyAgentCaseA(passkey: PasskeySigner, agentAddress: string): void {
  if (passkey.account.accountAddress !== agentAddress) {
    throw new Error(
      'Citizenship writes from the browser require the agent to be your profile smart wallet ' +
      '(sameWalletAsAgent). Use MCP for separate-key agents.',
    );
  }
}

async function signAndSubmit(
  passkey: PasskeySigner,
  agentAddress: string,
  built: BuiltTx,
): Promise<SubmitResult> {
  const signedXdr = await passkey.signUnsignedXdr(
    built.xdr,
    passkey.account,
    built.latestLedger,
  );
  return submitSignedCitizenshipTx(signedXdr, { agent: agentAddress });
}

/**
 * Run relinquish (when needed) → apply → USDC pay + confirm → poll
 * until geo-citizenship reports the target country active.
 */
export async function runResidencyChangeChain(args: {
  agentAddress: string;
  passkey: PasskeySigner;
  targetCountry: string;
  currentCountry?: string | null;
  onStep?: ResidencyChainStepHandler;
  pollTimeoutMs?: number;
}): Promise<{ country: string }> {
  const {
    agentAddress,
    passkey,
    targetCountry,
    currentCountry = null,
    onStep,
    pollTimeoutMs = 10_000,
  } = args;

  assertPasskeyAgentCaseA(passkey, agentAddress);

  const target = targetCountry.toUpperCase();
  if (!/^[A-Z]{2}$/.test(target)) {
    throw new Error('Target country must be a 2-letter ISO code.');
  }

  const current = currentCountry?.toUpperCase() || null;
  if (current === target) {
    return { country: target };
  }

  if (current) {
    onStep?.('relinquish', 'in_progress');
    try {
      const built = await buildRelinquishTx({ agent: agentAddress, country: current });
      const r = await signAndSubmit(passkey, agentAddress, built);
      onStep?.('relinquish', 'done', `tx ${r.hash.slice(0, 10)}…`);
    } catch (e) {
      onStep?.('relinquish', 'failed');
      throw e;
    }
  } else {
    onStep?.('relinquish', 'skipped');
  }

  onStep?.('apply', 'in_progress');
  try {
    const built = await buildApplyTx({
      agent:         agentAddress,
      country:       target,
      policyVersion: 1,
    });
    const r = await signAndSubmit(passkey, agentAddress, built);
    onStep?.('apply', 'done', `tx ${r.hash.slice(0, 10)}…`);
  } catch (e) {
    onStep?.('apply', 'failed');
    throw e;
  }

  onStep?.('confirm', 'in_progress');
  try {
    const payment = await buildPaymentTx({
      agent:        agentAddress,
      country:      target,
      payerAddress: agentAddress,
    });
    const signedXdr = await passkey.signUnsignedXdr(
      payment.unsignedXdr,
      passkey.account,
      payment.latestLedger,
    );
    const r = await confirmCitizenshipWithPasskey({
      agent:           agentAddress,
      country:         target,
      signedXdr,
      expiresAtLedger: payment.latestLedger + RESIDENCY_TTL_LEDGERS,
    });
    onStep?.('confirm', 'done', `tx ${r.hash.slice(0, 10)}… · paid ${payment.amountHuman}`);
  } catch (e) {
    onStep?.('confirm', 'failed');
    throw e;
  }

  onStep?.('poll', 'in_progress');
  const deadline = Date.now() + pollTimeoutMs;
  let landed = false;
  while (Date.now() < deadline) {
    await new Promise((res) => setTimeout(res, 1_000));
    const snap = await getResidencySnapshot(agentAddress).catch(() => null);
    if (snap?.country?.toUpperCase() === target) {
      landed = true;
      break;
    }
  }
  onStep?.('poll', landed ? 'done' : 'skipped', landed ? undefined : 'snapshot still stale');

  return { country: target };
}
